import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  t: (key: string) => string;
  changeLanguage: (lang: Language) => void;
  isInitialized: boolean;
}

const translations = {
  pt: {
    // Common
    'login': 'Entrar',
    'logout': 'Sair',
    'dashboard': 'Dashboard',
    'history': 'Histórico',
    'profile': 'Perfil',
    'welcome': 'Bem-vindo',
    'loading': 'Carregando...',
    'error': 'Erro',
    'success': 'Sucesso',
    'cancel': 'Cancelar',
    'save': 'Salvar',
    'edit': 'Editar',
    'delete': 'Excluir',
    'back': 'Voltar',
    'next': 'Próximo',
    'previous': 'Anterior',
    'finish': 'Finalizar',
    'start': 'Iniciar',
    'continue': 'Continuar',
    'yes': 'Sim',
    'no': 'Não',
    
    // Header
    'certify_lab': 'CertifyBase',
    'sign_out': 'Sair',
    'sign_in': 'Entrar',
    
    // Home page
    'hero_title': 'Conquiste sua',
    'hero_subtitle': 'Certificações Profissionais',
    'hero_description': 'Prepare-se para exames de certificação profissional com simulados realistas e análise detalhada do seu desempenho.',
    'start_kcna_sim': 'Começar Simulado',
    'view_syllabus': 'Ver Conteúdo Programático',
    'why_choose_platform': 'Por que escolher nossa plataforma?',
    'platform_description': 'Desenvolvida por especialistas, nossa plataforma oferece a preparação mais completa para suas certificações profissionais.',
    
    // Features
    'realistic_questions': 'Questões Realistas',
    'realistic_questions_desc': 'Simulados que reproduzem fielmente o formato e dificuldade do exame oficial.',
    'detailed_analysis': 'Análise Detalhada',
    'detailed_analysis_desc': 'Relatórios completos do seu desempenho por domínio de conhecimento.',
    'real_timer': 'Cronômetro Real',
    'real_timer_desc': 'Pratique com o mesmo tempo limite do exame oficial para melhor preparação.',
    'continuous_progress': 'Progresso Contínuo',
    'continuous_progress_desc': 'Acompanhe sua evolução com histórico detalhado de tentativas e notas.',
    
    // Dashboard
    'welcome_user': 'Bem-vindo',
    'manage_sims': 'Gerencie seus simulados e acompanhe seu progresso',
    'total_attempts': 'Tentativas',
    'approvals': 'Aprovações',
    'general_average': 'Média',
    'approval_rate': 'Taxa Aprovação',
    'study_hours': 'Estudo',
    'available_sims': 'Disponíveis',
    'performance_analysis': 'Análise de Performance',
    'exam_catalog': 'Catálogo de Simulados',
    'recent_activity': 'Atividade Recente',
    
    // Quiz
    'question': 'Questão',
    'of': 'de',
    'time_up': 'Tempo esgotado!',
    'exam_finished_auto': 'O simulado foi finalizado automaticamente.',
    'restricted_access': 'Acesso restrito',
    'need_login': 'Você precisa estar logado para fazer o simulado',
    'finish_quiz': 'Finalizar',
    'finish_exam': 'Finalizar Simulado',
    
    // History
    'sim_history': 'Histórico de Simulados',
    'track_progress': 'Acompanhe seu progresso e revise suas tentativas anteriores',
    'no_attempts': 'Nenhuma tentativa realizada ainda',
    'make_first_sim': 'Faça seu primeiro simulado!',
    'approved': 'Aprovado',
    'failed': 'Reprovado',
    'view_details': 'Ver Detalhes',
    
    // Results
    'congratulations': 'Parabéns!',
    'passed_exam': 'Você foi aprovado no simulado!',
    'better_luck': 'Continue estudando!',
    'not_passed': 'Você não atingiu a nota mínima desta vez.',
    'final_score': 'Pontuação Final',
    'time_spent': 'Tempo Gasto',
    'status': 'Status',
    'domain_performance': 'Performance por Domínio',
    
    // Auth
    'login_to_account': 'Entrar na sua conta',
    'email': 'Email',
    'password': 'Senha',
    'sign_up': 'Cadastrar',
    'already_have_account': 'Já tem uma conta?',
    'dont_have_account': 'Não tem uma conta?',
    
    // Landing Page - Hero Section
    'beta_version': 'Versão BETA',
    'master_professional_certifications': 'Domine as',
    'certifications': 'Certificações',
    'realistic_ai_simulations': 'Simulados realistas gerados por IA para as principais certificações de tecnologia.',
    'prepare_like_never_before': 'Prepare-se como nunca antes.',
    'start_now': 'Começar Agora',
    'how_it_works': 'Como Funciona',
    'available': 'Disponível',
    
    // Landing Page - How It Works
    'how_it_works_title': 'Como Funciona',
    'how_it_works_subtitle': '3 passos simples para começar sua jornada de certificação',
    'create_account': 'Criar Conta',
    'create_account_desc': 'Cadastre-se gratuitamente e tenha acesso completo à plataforma. Sem custos ocultos.',
    'choose_simulation': 'Escolher Simulado',
    'choose_simulation_desc': 'Selecione entre diversos simulados de certificações profissionais. Questões realistas geradas por IA.',
    'practice_like_real_exam': 'Praticar como na Prova Real',
    'practice_like_real_exam_desc': 'Simule o ambiente real do exame com cronômetro e análise detalhada. Prepare-se para o sucesso.',
    
    // Landing Page - Features
    'why_choose_our_platform': 'Por que Escolher Nossa Plataforma?',
    'why_choose_our_platform_subtitle': 'Tecnologia de ponta e experiência do usuário pensada para maximizar seu aprendizado',
    'realistic_questions_landing': 'Questões Realistas',
    'realistic_questions_landing_desc': 'Simulados que reproduzem fielmente o formato e dificuldade do exame oficial, gerados por inteligência artificial.',
    'detailed_analysis_landing': 'Análise Detalhada',
    'detailed_analysis_landing_desc': 'Relatórios completos do seu desempenho por domínio de conhecimento para identificar pontos de melhoria.',
    'real_timer_landing': 'Cronômetro Real',
    'real_timer_landing_desc': 'Pratique com o mesmo tempo limite do exame oficial para uma preparação mais eficaz e realista.',
    'continuous_progress_landing': 'Progresso Contínuo',
    'continuous_progress_landing_desc': 'Acompanhe sua evolução com histórico detalhado de tentativas, notas e áreas de melhoria.',
    
    // Landing Page - Story Section
    'our_story': 'Nossa História',
    'journey_that_brought_us_here': 'A Jornada que nos Trouxe até Aqui',
    'story_subtitle': 'De um projeto pessoal de estudos para uma plataforma que democratiza o acesso às melhores preparações para certificações',
    'story_beginning': 'Tudo começou',
    'story_beginning_text': 'como um projeto pessoal de estudos. Enquanto me preparava para minhas próprias certificações, percebi a dificuldade de encontrar simulados de alta qualidade que realmente reproduzissem a experiência do exame oficial.',
    'story_frustration': 'A frustração',
    'story_frustration_text': 'de estudar com questões desatualizadas ou que não refletiam a complexidade real dos exames me motivou a criar algo diferente. Algo que utilizasse a inteligência artificial para gerar questões realistas e análises detalhadas. E aqui está o resultado: este site foi desenvolvido completamente com IA, desde o planejamento inicial e criação de requisitos até a implementação final. Em apenas 3 dias, uma plataforma funcional estava no ar, demonstrando o poder transformador da inteligência artificial no desenvolvimento de software.',
    'story_mission': 'Hoje, nossa missão',
    'story_mission_text': 'é democratizar o acesso a simulados de alta qualidade, ajudando profissionais de tecnologia a conquistarem suas certificações com confiança e preparação adequada.',
    'powered_by_ai': 'Powered by AI',
    'powered_by_ai_desc': 'Questões geradas e revisadas por inteligência artificial para máxima precisão e realismo',
    'community': 'Comunidade',
    'community_desc': 'Milhares de profissionais já confiaram em nossa plataforma para suas certificações',
    'accessible': 'Acessível',
    'accessible_desc': 'Disponível 24/7, em qualquer lugar, para democratizar o acesso à educação de qualidade',
    
    // Landing Page - Available Simulations
    'start_your_preparation_now': 'Comece Sua Preparação Agora',
    'available_simulations_subtitle': 'Simulados profissionais disponíveis para as principais certificações de tecnologia',
    'professional_certifications_title': 'Certificações Profissionais - Cloud Native, ANBIMA, ITIL, SRE',
    'professional_certifications_desc': 'Simulado completo baseado no exame oficial da Linux Foundation. Teste seus conhecimentos em Kubernetes, arquitetura cloud native e ferramentas do ecossistema CNCF.',
    'duration': 'Duração',
    'questions_count': 'Questões',
    'difficulty': 'Dificuldade',
    'intermediate': 'Intermediário',
    'minutes': 'minutos',
    
    // Landing Page - Final CTA
    'ready_to_achieve_certification': 'Pronto para Conquistar sua Certificação?',
    'join_thousands_professionals': 'Junte-se a milhares de profissionais que já confiaram em nossa plataforma para suas preparações de certificação',
    'get_started': 'Começar Agora',
    
    // Landing Page - User Welcome
    'welcome_back': 'Bem-vindo de volta,',
    'continue_certification_journey': 'Continue sua jornada de certificação com nossos simulados profissionais',
    'certification_catalog': 'Catálogo de Certificações',
    
    // Gamification
    'gamification_system': 'Sistema de Gamificação',
    'learn_fun_motivating': 'Aprenda de forma divertida e motivadora',
    'transform_preparation': 'Transforme sua preparação em uma experiência envolvente com pontuação inteligente, conquistas e rankings',
    'smart_scoring': 'Pontuação Inteligente',
    'smart_scoring_desc': 'Ganhe pontos baseados em velocidade, precisão, domínios difíceis e sequências de acertos',
    'total_platform_points': 'Pontos Totais da Plataforma',
    'achievements': 'Conquistas',
    'achievements_desc': 'Desbloqueie badges especiais por marcos únicos e conquistas extraordinárias',
    'achievements_unlocked': 'Conquistas Desbloqueadas',
    'rankings': 'Rankings',
    'rankings_desc': 'Compete com outros usuários e veja sua posição no ranking global',
    'users_competing': 'Usuários Competindo',
    'connect_without_password': 'Conectar sem senha',
    'arena_of_achievements': 'Arena de Conquistas',
    'compete_professionals': 'Compete com outros profissionais, desbloqueie conquistas e suba no ranking global!',
    'total_points': 'Pontos Totais',
    'simulados_completos': 'simulados completos',
    'accuracy': 'Precisão',
    'average_hits': 'Média de acertos',
    'sequence': 'Sequência',
    'progress': 'Progresso',
    'general_progress': 'Progresso Geral',
    'completed_simulations': 'Simulados Completos',
    'average_accuracy': 'Precisão Média',
    'current_sequence': 'Sequência Atual',
    'leaders_ranking': 'Ranking de Líderes',
    'best_performers': 'Os melhores performers da plataforma',
    
    // Landing Page Stats
    'simulations': 'Simulados',
    'questions': 'Questões',
    'users': 'Usuários',
    'attempts': 'Tentativas',
    'average_score': 'Pontuação Média',
    'explore_gamification': 'Explorar Gamificação',
    'start_simulation': 'Iniciar Simulado',
    
    // Footer
    'developed_with': 'Desenvolvido com',
    'and_ai': 'e muita IA para democratizar o acesso à educação',
    'and_ai_mobile': 'e IA para democratizar a educação',
    'by': 'Por',
    'author_name': 'Ademar Gregio Jr.',
    
    // Magic Link Modal
    'magic_link': 'Link Mágico',
    'magic_link_desc': 'Receba um link único por e-mail para fazer login de forma segura',
    'email_placeholder': 'Digite seu e-mail',
    'send_magic_link': 'Enviar Link Mágico',
    'magic_link_sent': 'Link Mágico Enviado!',
    'check_email': 'Verifique seu e-mail e clique no link para fazer login. Verifique também sua pasta de spam.',
    'close': 'Fechar',
    'please_enter_email': 'Por favor, digite seu e-mail',
    
    // Missing translations from images
    'cloud_native_simulations': 'Simulados Cloud Native',
    'access_account_to_start': 'Acesse sua conta para começar',
    'enter_platform': 'Entrar na Plataforma',
    'login_or_create_account': 'Faça login ou crie sua conta',
    'login_tab': 'Entrar',
    'signup_tab': 'Cadastrar',
    'password_placeholder': 'Digite sua senha',
    'password_min_placeholder': 'Mínimo 6 caracteres',
    'confirm_password_placeholder': 'Confirme sua senha',
    'signup_success': 'Conta criada com sucesso!',
    'check_email_to_confirm': 'Verifique seu e-mail para confirmar a conta',
    'your_permissions': 'Suas Permissões',
    'no_special_permissions': 'Nenhuma permissão especial',
    'access_only_public_content': 'Você tem acesso apenas aos vendors e certificações públicos.',
    'overall_average': 'Média Geral',
    'overall_performance': 'Performance Geral',
    'no_performance_data': 'Nenhum dado de performance',
    'view_complete_history': 'Ver Histórico Completo',
    'choose_your_certification': 'Escolha sua certificação',
    'explore_hundreds_certifications': 'Explore centenas de certificações das principais tecnologias',
    'vendors_found': 'vendors encontrados',
    'explore_certifications': 'Explorar Certificações',
    
    // OAuth buttons
    'sign_in_with_github': 'Entrar com GitHub',
    'sign_in_with_google': 'Entrar com Google', 
    'sign_in_with_linkedin': 'Entrar com LinkedIn',
    'continue_with': 'Ou continue com',
    
    // Loading and success messages
    'login_success': 'Login realizado com sucesso',
    
    // Login error messages
    'login_error': 'Erro no Login',
    'invalid_credentials': 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.',
    'unknown_login_error': 'Erro desconhecido no login. Tente novamente.',
    'account_deactivated': 'Sua conta foi desativada. Entre em contato com o suporte.',
    'github_login_error': 'Erro ao fazer login com GitHub',
    'github_login_error_title': 'Erro no Login GitHub',
    'google_login_error': 'Erro ao fazer login com Google',
    'google_login_error_title': 'Erro no Login Google',
    'linkedin_login_error': 'Erro ao fazer login com LinkedIn',
    'linkedin_login_error_title': 'Erro no Login LinkedIn',
    
    // Search and sorting
    'search_vendors_technologies_certifications': 'Buscar vendors, tecnologias ou certificações...',
    'sort_by': 'Ordenar por',
    'name_a_z': 'Nome A-Z',
    
    // Dashboard - Missing translations
    'complete_simulations_to_see_analysis': 'Complete simulados para ver análise',
    'welcome_back_user': 'Bem-vindo de volta',
    'manage_simulations_progress': 'Gerencie seus simulados e acompanhe seu progresso',
    'simulations_count': 'Simulados',
    'attempts_count': 'Tentativas',
    'approval_percentage': 'Taxa Aprovação',
    'simulations_catalog': 'Catálogo de Simulados',
    'explore_all_simulations': 'Explorar Todos os Simulados',
    'simulations_available': 'Simulados Disponíveis',
    'simulations_started': 'Simulados Iniciados',
    'completed_attempts': 'Tentativas Completas',
    
    // Study Tips
    'study_tips': 'Dicas de Estudo',
    'maximize_preparation_strategies': 'Estratégias para maximizar sua preparação',
    'study_consistently': 'Estude Consistentemente',
    'dedicate_regular_time': 'Dedique tempo regular aos estudos para manter o ritmo',
    'focus_weak_points': 'Foque nos Pontos Fracos',
    'use_reports_identify_improve': 'Use os relatórios para identificar e melhorar áreas específicas',
    'practice_timing': 'Pratique o Timing',
    'do_timed_simulations': 'Faça simulados cronometrados para se acostumar com o tempo',
    'repeat_simulations': 'Repita os Simulados',
    'redo_simulations_reinforce': 'Refaça simulados para reforçar o aprendizado',
    
    // How it works
    'understand_maximize_simulations': 'Entenda como maximizar seus simulados',
    'select_simulation_practice': 'Selecione o simulado que deseja praticar',
    'answer_questions': 'Responder Questões',
    'answer_all_questions_time': 'Responda todas as questões dentro do tempo',
    'analyze_result': 'Analisar Resultado',
    'see_performance_domains': 'Veja sua performance por domínios',
    
    // History
    'simulation_history': 'Histórico de Simulados',
    'track_attempts_evolution': 'Acompanhe suas tentativas e evolução',
    'detailed_question_review': 'Revisão Detalhada das Questões',
    'questions_correct_incorrect': 'questões corretas e incorretas',
    
    // Vendors count
    'vendors_available': 'vendors disponíveis',
    
    // Additional missing translations
    'summary_by_certification': 'Resumo por Certificação',
    'simulations_count_lower': 'simulações',
    'attempts_count_lower': 'tentativas',
    'approval_percentage_lower': 'aprovação',
    'study_time': 'Tempo de Estudo',
    'Média': 'Média',
    'administrator': 'Administrador',
    
    // Additional missing translations from user feedback
    'loading_vendors': 'Carregando vendors...',
    'all': 'Todas',
    'overall_average_score': 'Pontuação média geral',
    'domains_to_improve': 'Domínios para Melhorar',
    'domains_below_threshold': 'Áreas com desempenho abaixo de 70% ou da sua média geral',
    'strong_domains': 'Domínios Fortes',
    'domains_performing_well': 'Áreas onde você está performando bem',
    'view_all': 'Ver Todas',
    'loading_history': 'Carregando histórico...',
    'search_attempts': 'Buscar tentativas...',
    'CERTIFICATIONS_SHORT': 'CERT.',
    'SIMI': 'SIM.',
    
    // Dashboard loading states
    'loading_dashboard': 'Carregando dashboard...',
    'preparing_your_data': 'Preparando seus dados...',
    'adaptive_engine_active': 'Engine adaptativo ativo',
    'attempts_label': 'Tentativas',
    'approval_label': 'Aprovação',
    'certifications_short': 'Certificações',
    'simulations_short': 'Simulados',
    
    // Search and review translations
    'searching_attempts': 'Buscando tentativas...',
    'details': 'Detalhes',
    'review': 'Revisar',
    'justification': 'Justificativa',
    'all_certifications': 'Todas as Certificações',
    'available_simulations': 'Simulados Disponíveis',
    'reinforce': 'Reforçar',
    
    // Sorting options
    'more_certifications': 'Mais certificações',
    'more_simulations': 'Mais simulados',
    'original_order': 'Ordem original',
    
    // Missing translations for login and history
    'signing_in': 'Entrando...',
    'connecting': 'Conectando...',
    'signing_up': 'Cadastrando...',
    'sign_up_button': 'Cadastrar',
    'your_answer_correct': 'Sua resposta (correta)',
    'your_answer_incorrect': 'Sua resposta (incorreta)',
    'correct_answer': 'Resposta correta',
    'your_answer': 'Sua resposta',
    'correct': 'Correto',
    'incorrect': 'Incorreto',
    'justification_not_available': 'Justificativa não disponível',
    'loading_questions': 'Carregando questões...',
    'searching_questions_review': 'Buscando questões para revisão...',
    'student': 'Estudante',
    'certification': 'Certificação',
    'selected_certification': 'Certificação Selecionada',
    'complete_first_simulation': 'Complete seu primeiro simulado',
    'performance_analysis_will_appear': 'A análise de performance aparecerá aqui',
    'active_filter': 'Filtro ativo:',
    'filtered': 'Filtrado',
    'start_studies': 'Iniciar Estudos',
    'ready_to_start': 'Pronto para começar?',
    'choose_simulation_test_knowledge': 'Escolha um simulado e teste seus conhecimentos',
    'explore_simulations': 'Explorar Simulados',
    'filter_by_certification': 'Filtrar por certificação',
    'no_certifications_found': 'Nenhuma certificação encontrada',
    'no_simulations_found': 'Nenhum simulado encontrado',
    'no_simulations_available': 'Nenhum simulado disponível no momento',
    'no_simulations_for_certification': 'Nenhum simulado disponível para esta certificação',
    'simulation': 'Simulado',
    'score': 'Pontuação',
    'performance_by_domain': 'Performance por Domínio',
    'no_attempts_found': 'Nenhuma tentativa encontrada',
    'no_simulations_completed': 'Você ainda não completou nenhum simulado',
    'take_first_simulation': 'Fazer Primeiro Simulado',
    'back_to_history': 'Voltar ao Histórico',
    'attempt_completed_on': 'Tentativa concluída em'
  },
  en: {
    // Common
    'login': 'Login',
    'logout': 'Logout',
    'dashboard': 'Dashboard',
    'history': 'History',
    'profile': 'Profile',
    'welcome': 'Welcome',
    'loading': 'Loading...',
    'error': 'Error',
    'success': 'Success',
    'cancel': 'Cancel',
    'save': 'Save',
    'edit': 'Edit',
    'delete': 'Delete',
    'back': 'Back',
    'next': 'Next',
    'previous': 'Previous',
    'finish': 'Finish',
    'start': 'Start',
    'continue': 'Continue',
    'yes': 'Yes',
    'no': 'No',
    
    // Header
    'certify_lab': 'CertifyBase',
    'sign_out': 'Sign Out',
    'sign_in': 'Sign In',
    
    // Home page
    'hero_title': 'Achieve your',
    'hero_subtitle': 'Professional Certifications',
    'hero_description': 'Prepare for professional certification exams with realistic simulations and detailed performance analysis.',
    'start_kcna_sim': 'Start Simulation',
    'view_syllabus': 'View Syllabus',
    'why_choose_platform': 'Why choose our platform?',
    'platform_description': 'Developed by experts, our platform offers the most complete preparation for your professional certifications.',
    
    // Features
    'realistic_questions': 'Realistic Questions',
    'realistic_questions_desc': 'Simulations that faithfully reproduce the format and difficulty of the official exam.',
    'detailed_analysis': 'Detailed Analysis',
    'detailed_analysis_desc': 'Complete reports of your performance by knowledge domain.',
    'real_timer': 'Real Timer',
    'real_timer_desc': 'Practice with the same time limit as the official exam for better preparation.',
    'continuous_progress': 'Continuous Progress',
    'continuous_progress_desc': 'Track your evolution with detailed history of attempts and scores.',
    
    // Dashboard
    'welcome_user': 'Welcome',
    'manage_sims': 'Manage your simulations and track your progress',
    'total_attempts': 'Attempts',
    'approvals': 'Approvals',
    'general_average': 'Average',
    'approval_rate': 'Approval Rate',
    'study_hours': 'Study',
    'available_sims': 'Available',
    'performance_analysis': 'Performance Analysis',
    'exam_catalog': 'Exam Catalog',
    'recent_activity': 'Recent Activity',
    
    // Quiz
    'question': 'Question',
    'of': 'of',
    'time_up': 'Time is up!',
    'exam_finished_auto': 'The simulation was automatically finished.',
    'restricted_access': 'Restricted access',
    'need_login': 'You need to be logged in to take the simulation',
    'finish_quiz': 'Finish',
    'finish_exam': 'Finish Exam',
    
    // History
    'sim_history': 'Simulation History',
    'track_progress': 'Track your progress and review your previous attempts',
    'no_attempts': 'No attempts made yet',
    'make_first_sim': 'Take your first simulation!',
    'approved': 'Passed',
    'failed': 'Failed',
    'view_details': 'View Details',
    
    // Results
    'congratulations': 'Congratulations!',
    'passed_exam': 'You passed the simulation!',
    'better_luck': 'Keep studying!',
    'not_passed': 'You did not reach the minimum score this time.',
    'final_score': 'Final Score',
    'time_spent': 'Time Spent',
    'status': 'Status',
    'domain_performance': 'Performance by Domain',
    
    // Auth
    'login_to_account': 'Sign in to your account',
    'email': 'Email',
    'password': 'Password',
    'sign_up': 'Sign Up',
    'already_have_account': 'Already have an account?',
    'dont_have_account': 'Don\'t have an account?',
    
    // Landing Page - Hero Section
    'beta_version': 'BETA Version',
    'master_professional_certifications': 'Master Professional',
    'certifications': 'Certifications',
    'realistic_ai_simulations': 'Realistic AI-generated simulations for the main technology certifications.',
    'prepare_like_never_before': 'Prepare like never before.',
    'start_now': 'Start Now',
    'how_it_works': 'How It Works',
    'available': 'Available',
    
    // Landing Page - How It Works
    'how_it_works_title': 'How It Works',
    'how_it_works_subtitle': '3 simple steps to start your certification journey',
    'create_account': 'Create Account',
    'create_account_desc': 'Sign up for free and get full access to the platform. No hidden costs.',
    'choose_simulation': 'Choose Simulation',
    'choose_simulation_desc': 'Select from various professional certification simulations. Realistic questions generated by AI.',
    'practice_like_real_exam': 'Practice Like Real Exam',
    'practice_like_real_exam_desc': 'Simulate the real exam environment with timer and detailed analysis. Prepare for success.',
    
    // Landing Page - Features
    'why_choose_our_platform': 'Why Choose Our Platform?',
    'why_choose_our_platform_subtitle': 'Cutting-edge technology and user experience designed to maximize your learning',
    'realistic_questions_landing': 'Realistic Questions',
    'realistic_questions_landing_desc': 'Simulations that faithfully reproduce the format and difficulty of the official exam, generated by artificial intelligence.',
    'detailed_analysis_landing': 'Detailed Analysis',
    'detailed_analysis_landing_desc': 'Complete reports of your performance by knowledge domain to identify improvement points.',
    'real_timer_landing': 'Real Timer',
    'real_timer_landing_desc': 'Practice with the same time limit as the official exam for more effective and realistic preparation.',
    'continuous_progress_landing': 'Continuous Progress',
    'continuous_progress_landing_desc': 'Track your evolution with detailed history of attempts, scores and improvement areas.',
    
    // Landing Page - Story Section
    'our_story': 'Our Story',
    'journey_that_brought_us_here': 'The Journey That Brought Us Here',
    'story_subtitle': 'From a personal study project to a platform that democratizes access to the best certification preparations',
    'story_beginning': 'It all started',
    'story_beginning_text': 'as a personal study project. While preparing for my own certifications, I realized the difficulty of finding high-quality simulations that truly reproduced the official exam experience.',
    'story_frustration': 'The frustration',
    'story_frustration_text': 'of studying with outdated questions or that didn\'t reflect the real complexity of exams motivated me to create something different. Something that would use artificial intelligence to generate realistic questions and detailed analysis. And here\'s the result: this entire website was developed completely with AI, from initial planning and requirements creation to final implementation. In just 3 days, a functional platform was live, demonstrating the transformative power of artificial intelligence in software development.',
    'story_mission': 'Today, our mission',
    'story_mission_text': 'is to democratize access to high-quality simulations, helping technology professionals achieve their certifications with confidence and proper preparation.',
    'powered_by_ai': 'Powered by AI',
    'powered_by_ai_desc': 'Questions generated and reviewed by artificial intelligence for maximum accuracy and realism',
    'community': 'Community',
    'community_desc': 'Thousands of professionals have already trusted our platform for their certifications',
    'accessible': 'Accessible',
    'accessible_desc': 'Available 24/7, anywhere, to democratize access to quality education',
    
    // Landing Page - Available Simulations
    'start_your_preparation_now': 'Start Your Preparation Now',
    'available_simulations_subtitle': 'Professional simulations available for the main technology certifications',
    'professional_certifications_title': 'Professional Certifications - Cloud Native, ANBIMA, ITIL, SRE',
    'professional_certifications_desc': 'Complete simulation based on the official Linux Foundation exam. Test your knowledge in Kubernetes, cloud native architecture and CNCF ecosystem tools.',
    'duration': 'Duration',
    'questions_count': 'Questions',
    'difficulty': 'Difficulty',
    'intermediate': 'Intermediate',
    'minutes': 'minutes',
    
    // Landing Page - Final CTA
    'ready_to_achieve_certification': 'Ready to Achieve Your Certification?',
    'join_thousands_professionals': 'Join thousands of professionals who have already trusted our platform for their certification preparations',
    'get_started': 'Get Started',
    
    // Landing Page - User Welcome
    'welcome_back': 'Welcome back,',
    'continue_certification_journey': 'Continue your certification journey with our professional simulations',
    'certification_catalog': 'Certification Catalog',
    
    // Missing translations from images
    'cloud_native_simulations': 'Cloud Native Simulations',
    'access_account_to_start': 'Access your account to start',
    'enter_platform': 'Enter Platform',
    'login_or_create_account': 'Login or create your account',
    'login_tab': 'Login',
    'signup_tab': 'Sign Up',
    'password_placeholder': 'Enter your password',
    'password_min_placeholder': 'Minimum 6 characters',
    'confirm_password_placeholder': 'Confirm your password',
    'signup_success': 'Account created successfully!',
    'check_email_to_confirm': 'Check your email to confirm the account',
    'your_permissions': 'Your Permissions',
    'no_special_permissions': 'No special permissions',
    'access_only_public_content': 'You have access only to public vendors and certifications.',
    'overall_average': 'Overall Average',
    'overall_performance': 'Overall Performance',
    'no_performance_data': 'No performance data',
    'view_complete_history': 'View Complete History',
    'choose_your_certification': 'Choose your certification',
    'explore_hundreds_certifications': 'Explore hundreds of certifications from leading technologies',
    'vendors_found': 'vendors found',
    'explore_certifications': 'Explore Certifications',
    
    // OAuth buttons
    'sign_in_with_github': 'Sign in with GitHub',
    'sign_in_with_google': 'Sign in with Google', 
    'sign_in_with_linkedin': 'Sign in with LinkedIn',
    'continue_with': 'Or continue with',
    
    // Loading and success messages
    'login_success': 'Login successful',
    
    // Login error messages
    'login_error': 'Login Error',
    'invalid_credentials': 'Incorrect email or password. Please check your credentials and try again.',
    'unknown_login_error': 'Unknown login error. Please try again.',
    'account_deactivated': 'Your account has been deactivated. Please contact support.',
    'github_login_error': 'Error signing in with GitHub',
    'github_login_error_title': 'GitHub Login Error',
    'google_login_error': 'Error signing in with Google',
    'google_login_error_title': 'Google Login Error',
    'linkedin_login_error': 'Error signing in with LinkedIn',
    'linkedin_login_error_title': 'LinkedIn Login Error',
    
    // Search and sorting
    'search_vendors_technologies_certifications': 'Search vendors, technologies or certifications...',
    'sort_by': 'Sort by',
    'name_a_z': 'Name A-Z',
    
    // Dashboard - Missing translations
    'complete_simulations_to_see_analysis': 'Complete simulations to see analysis',
    'welcome_back_user': 'Welcome back',
    'manage_simulations_progress': 'Manage your simulations and track your progress',
    'simulations_count': 'Simulations',
    'attempts_count': 'Attempts',
    'approval_percentage': 'Approval Rate',
    'simulations_catalog': 'Simulations Catalog',
    'explore_all_simulations': 'Explore All Simulations',
    'simulations_available': 'Available Simulations',
    'simulations_started': 'Started Simulations',
    'completed_attempts': 'Completed Attempts',
    
    // Study Tips
    'study_tips': 'Study Tips',
    'maximize_preparation_strategies': 'Strategies to maximize your preparation',
    'study_consistently': 'Study Consistently',
    'dedicate_regular_time': 'Dedicate regular time to studies to maintain pace',
    'focus_weak_points': 'Focus on Weak Points',
    'use_reports_identify_improve': 'Use reports to identify and improve specific areas',
    'practice_timing': 'Practice Timing',
    'do_timed_simulations': 'Do timed simulations to get used to the time pressure',
    'repeat_simulations': 'Repeat Simulations',
    'redo_simulations_reinforce': 'Redo simulations to reinforce learning',
    
    // How it works
    'understand_maximize_simulations': 'Understand how to maximize your simulations',
    'select_simulation_practice': 'Select the simulation you want to practice',
    'answer_questions': 'Answer Questions',
    'answer_all_questions_time': 'Answer all questions within the time limit',
    'analyze_result': 'Analyze Result',
    'see_performance_domains': 'See your performance by domains',
    
    // History
    'simulation_history': 'Simulation History',
    'track_attempts_evolution': 'Track your attempts and evolution',
    'detailed_question_review': 'Detailed Question Review',
    'questions_correct_incorrect': 'correct and incorrect questions',
    
    // Vendors count
    'vendors_available': 'vendors available',
    
    // Gamification
    'gamification_system': 'Gamification System',
    'learn_fun_motivating': 'Learn in a fun and motivating way',
    'transform_preparation': 'Transform your preparation into an engaging experience with smart scoring, achievements and rankings',
    'smart_scoring': 'Smart Scoring',
    'smart_scoring_desc': 'Earn points based on speed, accuracy, difficult domains and hit sequences',
    'total_platform_points': 'Total Platform Points',
    'achievements': 'Achievements',
    'achievements_desc': 'Unlock special badges for unique milestones and extraordinary achievements',
    'achievements_unlocked': 'Achievements Unlocked',
    'rankings': 'Rankings',
    'rankings_desc': 'Compete with other users and see your position in the global ranking',
    'users_competing': 'Users Competing',
    'connect_without_password': 'Connect without password',
    'arena_of_achievements': 'Arena of Achievements',
    'compete_professionals': 'Compete with other professionals, unlock achievements and climb the global ranking!',
    'total_points': 'Total Points',
    'simulados_completos': 'simulations completed',
    'accuracy': 'Accuracy',
    'average_hits': 'Average hits',
    'sequence': 'Sequence',
    'progress': 'Progress',
    'general_progress': 'General Progress',
    'completed_simulations': 'Completed Simulations',
    'average_accuracy': 'Average Accuracy',
    'current_sequence': 'Current Sequence',
    'leaders_ranking': 'Leaders Ranking',
    'best_performers': 'The best performers on the platform',
    
    // Landing Page Stats
    'simulations': 'Simulations',
    'questions': 'Questions',
    'users': 'Users',
    'attempts': 'Attempts',
    'average_score': 'Average Score',
    'explore_gamification': 'Explore Gamification',
    'start_simulation': 'Start Simulation',
    
    // Footer
    'developed_with': 'Developed with',
    'and_ai': 'and lots of AI to democratize access to education',
    'and_ai_mobile': 'and AI to democratize education',
    'by': 'By',
    'author_name': 'Ademar Gregio Jr.',
    
    // Magic Link Modal
    'magic_link': 'Magic Link',
    'magic_link_desc': 'Receive a unique link by email to login securely',
    'email_placeholder': 'Enter your email',
    'send_magic_link': 'Send Magic Link',
    'magic_link_sent': 'Magic Link Sent!',
    'check_email': 'Check your email and click the link to login. Also check your spam folder.',
    'close': 'Close',
    'please_enter_email': 'Please enter your email',
    
    // Additional missing translations
    'summary_by_certification': 'Summary by Certification',
    'simulations_count_lower': 'simulations',
    'attempts_count_lower': 'attempts',
    'approval_percentage_lower': 'approval',
    'study_time': 'Study Time',
    'Média': 'Average',
    'administrator': 'Administrator',
    
    // Additional missing translations from user feedback
    'loading_vendors': 'Loading vendors...',
    'all': 'All',
    'overall_average_score': 'Overall average score',
    'domains_to_improve': 'Domains to Improve',
    'domains_below_threshold': 'Areas with performance below 70% or your overall average',
    'strong_domains': 'Strong Domains',
    'domains_performing_well': 'Areas where you are performing well',
    'view_all': 'View All',
    'loading_history': 'Loading history...',
    'search_attempts': 'Search attempts...',
    'CERTIFICATIONS_SHORT': 'CERT.',
    'SIMI': 'SIM.',
    
    // Dashboard loading states
    'loading_dashboard': 'Loading dashboard...',
    'preparing_your_data': 'Preparing your data...',
    'adaptive_engine_active': 'Adaptive engine active',
    'attempts_label': 'Attempts',
    'approval_label': 'Approval',
    'certifications_short': 'Certifications',
    'simulations_short': 'Simulations',
    
    // Search and review translations
    'searching_attempts': 'Searching attempts...',
    'details': 'Details',
    'review': 'Review',
    'justification': 'Justification',
    'all_certifications': 'All Certifications',
    'available_simulations': 'Available Simulations',
    'reinforce': 'Reinforce',
    
    // Sorting options
    'more_certifications': 'More certifications',
    'more_simulations': 'More simulations',
    'original_order': 'Original order',
    
    // Missing translations for login and history
    'signing_in': 'Signing in...',
    'connecting': 'Connecting...',
    'signing_up': 'Signing up...',
    'sign_up_button': 'Sign Up',
    'your_answer_correct': 'Your answer (correct)',
    'your_answer_incorrect': 'Your answer (incorrect)',
    'correct_answer': 'Correct answer',
    'your_answer': 'Your answer',
    'correct': 'Correct',
    'incorrect': 'Incorrect',
    'justification_not_available': 'Justification not available',
    'loading_questions': 'Loading questions...',
    'searching_questions_review': 'Searching questions for review...',
    'student': 'Student',
    'certification': 'Certification',
    'selected_certification': 'Selected Certification',
    'complete_first_simulation': 'Complete your first simulation',
    'performance_analysis_will_appear': 'Performance analysis will appear here',
    'active_filter': 'Active filter:',
    'filtered': 'Filtered',
    'start_studies': 'Start Studies',
    'ready_to_start': 'Ready to start?',
    'choose_simulation_test_knowledge': 'Choose a simulation and test your knowledge',
    'explore_simulations': 'Explore Simulations',
    'filter_by_certification': 'Filter by certification',
    'no_certifications_found': 'No certifications found',
    'no_simulations_found': 'No simulations found',
    'no_simulations_available': 'No simulations available at the moment',
    'no_simulations_for_certification': 'No simulations available for this certification',
    'simulation': 'Simulation',
    'score': 'Score',
    'performance_by_domain': 'Performance by Domain',
    'no_attempts_found': 'No attempts found',
    'no_simulations_completed': 'You have not completed any simulations yet',
    'take_first_simulation': 'Take First Simulation',
    'back_to_history': 'Back to History',
    'attempt_completed_on': 'Attempt completed on'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>('pt');
  const [isInitialized, setIsInitialized] = useState(false);

  // Detectar idioma preferido
  const detectPreferredLanguage = (): Language => {
    // 1. Verificar localStorage
    const stored = localStorage.getItem('preferred-language');
    if (stored === 'pt' || stored === 'en') {
      return stored;
    }
    
    // 2. Verificar idioma do navegador
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('en')) {
      return 'en';
    }
    
    // 3. Padrão português
    return 'pt';
  };

  // Inicializar idioma
  useEffect(() => {
    const preferredLanguage = detectPreferredLanguage();
    setLanguage(preferredLanguage);
    setIsInitialized(true);
  }, []);

  // Persistir mudanças de idioma
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('preferred-language', language);
    }
  }, [language, isInitialized]);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('preferred-language', lang);
  };
  
  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  const contextValue: LanguageContextType = {
    language,
    t,
    changeLanguage,
    isInitialized
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};